import React, { useState, useEffect, useRef, useMemo, MouseEvent } from 'react';
import { Plus, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import utilityService from '../../../shared/utils/utilityService';
import { useTime } from '../../../shared/context/TimeContext';
import { useAuth } from '../../auth/context/AuthContext';
import { formatTo12Hour, parseTimeToSeconds, calculateRemainingSeconds, Element } from '@thekwallace/backtime-shared';
import { flattenLeafElements } from '../utils/grouping';

interface CountdownTimerProps {
  selectedStartTime: string | null;
  occurrenceStartAbsolute?: number | null;
  elements?: Element[];
  timeOffset?: number;
  onTimeOffsetChange?: (offset: number) => void;
  isPublic?: boolean;
  publicCode?: string | null;
  readOnly?: boolean;
  noUpcomingOccurrences?: boolean;
  isOccurrenceComplete?: boolean;
  dayName?: string | null;
}

interface TimeDisplayState {
  timeRemaining: string;
  description: string;
  currentTime: string;
  remainingSeconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  selectedStartTime,
  occurrenceStartAbsolute = null,
  elements = [],
  timeOffset = 0,
  onTimeOffsetChange,
  isPublic = false,
  publicCode = '',
  readOnly = false,
  noUpcomingOccurrences = false,
  isOccurrenceComplete = false,
  dayName = null
}) => {
  // UI state (toggle states only)
  const [showClock, setShowClock] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Get synchronized time from centralized TimeContext (updates every second)
  const { getTimeWithOffset } = useTime();
  const currentSeconds = getTimeWithOffset(timeOffset);

  // Check authentication for showing offset controls
  const { currentUser } = useAuth();
  const isAuthenticated = !!currentUser;

  // Sync state with browser fullscreen changes (e.g., ESC key exit)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Compute all time-related display values from centralized time
  const { timeRemaining, description, currentTime, remainingSeconds }: TimeDisplayState = useMemo(() => {
    // Calculate current clock time display using shared utility
    const clockTime = formatTo12Hour(currentSeconds, true);

    // Normalized time for non-absolute calculations
    const normalizedCurrent = ((currentSeconds % 86400) + 86400) % 86400;

    // Handle completed occurrences (manual mode viewing historical occurrences)
    if (isOccurrenceComplete) {
      return { timeRemaining: '--:--', description: 'Occurrence complete', currentTime: clockTime, remainingSeconds: 0 };
    }

    // Handle no occurrences within window
    if (noUpcomingOccurrences) {
      return { timeRemaining: '--:--', description: 'Nothing upcoming', currentTime: clockTime, remainingSeconds: 0 };
    }

    // Handle empty or invalid selectedStartTime
    if (!selectedStartTime || selectedStartTime.trim() === '') {
      return { timeRemaining: '--:--', description: 'Set a start time', currentTime: clockTime, remainingSeconds: 0 };
    }

    // Use absolute seconds if provided, otherwise fall back to normalized
    const occurrenceStartSeconds = occurrenceStartAbsolute !== null
      ? occurrenceStartAbsolute
      : parseTimeToSeconds(selectedStartTime);

    // Use matching current time (absolute if occurrence is absolute, normalized otherwise)
    const currentForCalc = occurrenceStartAbsolute !== null ? currentSeconds : normalizedCurrent;

    // Calculate countdown with day-aware logic.
    // Flatten groups to leaves in tree order — sortOrder is sibling-scoped after grouping,
    // so the raw elements would sequence the next-up element incorrectly.
    const nextElement = utilityService.getNextTimeElement(currentForCalc, occurrenceStartSeconds, flattenLeafElements(elements));

    if (nextElement) {
      // Show countdown to next upcoming element
      const remaining = calculateRemainingSeconds(currentForCalc, nextElement.startTime);
      return {
        timeRemaining: utilityService.formatCountdown(remaining),
        description: nextElement.description,
        currentTime: clockTime,
        remainingSeconds: remaining
      };
    } else if (currentForCalc < occurrenceStartSeconds) {
      // Before occurrence starts - show countdown to occurrence start
      const remaining = calculateRemainingSeconds(currentForCalc, occurrenceStartSeconds);
      return {
        timeRemaining: utilityService.formatCountdown(remaining),
        description: 'Occurrence starts',
        currentTime: clockTime,
        remainingSeconds: remaining
      };
    } else {
      // All elements have started - occurrence is happening now
      return {
        timeRemaining: '0:00',
        description: 'Happening Now',
        currentTime: clockTime,
        remainingSeconds: 0
      };
    }
  }, [currentSeconds, selectedStartTime, occurrenceStartAbsolute, elements, noUpcomingOccurrences, isOccurrenceComplete]);

  // Handle mouse movement to show/hide controls in fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    let timeout: NodeJS.Timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 5000);
    };

    handleMouseMove(); // Show controls initially
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [isFullscreen]);

  const handleAdjustOffset = (seconds: number, e: MouseEvent) => {
    e.stopPropagation();
    if (onTimeOffsetChange) {
      onTimeOffsetChange(timeOffset + seconds);
    }
  };

  const handleResetOffset = (e: MouseEvent) => {
    e.stopPropagation();
    if (onTimeOffsetChange) {
      onTimeOffsetChange(0);
    }
  };

  const handleFullscreenToggle = async (e: MouseEvent) => {
    e.stopPropagation();

    // Check both native fullscreen and CSS fallback state
    if (!document.fullscreenElement && !isFullscreen) {
      // Enter fullscreen
      try {
        if (fullscreenRef.current) {
          await fullscreenRef.current.requestFullscreen();
        }
      } catch (err) {
        console.error('Fullscreen error:', err);
        // Fallback to CSS fullscreen if API fails
        setIsFullscreen(true);
      }
    } else {
      // Exit fullscreen
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (err) {
          console.error('Exit fullscreen error:', err);
          setIsFullscreen(false);
        }
      } else {
        // CSS fallback mode - just update state
        setIsFullscreen(false);
      }
    }
  };

  const getCountdownTextColor = (): string => {
    if (remainingSeconds <= 120) { // 2 minutes or less - red
      return 'text-red-500';
    } else if (remainingSeconds <= 300) { // 5 minutes or less - yellow
      return 'text-yellow-500';
    } else { // more than 5 minutes - green
      return 'text-green-500';
    }
  };

  const getBorderColor = (): string => {
    if (remainingSeconds === 0) {
      return 'border-blue-700/50';
    } else if (remainingSeconds <= 120) {
      return 'border-red-700/50';
    } else if (remainingSeconds <= 300) {
      return 'border-yellow-700/50';
    } else {
      return 'border-green-700/50';
    }
  };

  // Build the full URL for public timeline sharing
  const timelineUrl = isPublic && publicCode ? `${window.location.origin}/t/${publicCode}` : '';

  if (isFullscreen) {
    return (
      <div ref={fullscreenRef} className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-8 cursor-pointer animate-fadeIn"
        onClick={() => setShowClock(!showClock)}
      >
        {/* QR Code in top-left corner */}
        {isPublic && timelineUrl && (
          <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-2xl z-10">
            <QRCodeSVG
              value={timelineUrl}
              size={120}
              level="M"
              includeMargin={false}
            />
          </div>
        )}

        <button
          onClick={handleFullscreenToggle}
          className={`absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-500 hover:scale-110 z-10 ${
            showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          title="Exit fullscreen"
        >
          <Minimize2 className="w-6 h-6 text-white" />
        </button>

        <div className="text-center animate-scaleIn">
          {showClock ? (
            <>
              <div className="text-9xl font-bold text-white mb-8 font-mono tracking-tight drop-shadow-2xl transition-all duration-300">
                {currentTime}
              </div>
              <div className="text-4xl text-blue-200 font-medium transition-all duration-300">
                <div className="flex flex-col items-center gap-6">
                  {isAuthenticated && onTimeOffsetChange && (
                    <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleAdjustOffset(-1, e)}
                        className="p-3 bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-300 hover:scale-110"
                        title="Decrease by 1 second"
                      >
                        <Minus className="w-6 h-6" />
                      </button>
                      <div className="text-xl text-gray-300 min-w-[120px]">
                        Offset: {timeOffset > 0 ? '+' : ''}{timeOffset}s
                      </div>
                      <button
                        onClick={(e) => handleAdjustOffset(1, e)}
                        className="p-3 bg-green-600 hover:bg-green-700 rounded-lg transition-all duration-300 hover:scale-110"
                        title="Increase by 1 second"
                      >
                        <Plus className="w-6 h-6" />
                      </button>
                      {timeOffset !== 0 && (
                        <button
                          onClick={handleResetOffset}
                          className="ml-3 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-all duration-300 hover:scale-105 text-base"
                          title="Reset offset to 0"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-4xl text-white font-bold mb-6 transition-all duration-300">
                {description}
              </div>
              {remainingSeconds > 0 ? (
                <>
                  <div className="text-3xl text-white/70 font-medium mb-6 transition-all duration-300">
                    Starts in
                  </div>
                  <div className={`text-9xl font-bold font-mono tracking-tight drop-shadow-2xl transition-all duration-300 ${getCountdownTextColor()}`}>
                    {timeRemaining}
                  </div>
                </>
              ) : !noUpcomingOccurrences ? (
                <div className="text-5xl font-bold text-state-active transition-all duration-300">
                  Happening Now
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={fullscreenRef}
      className="relative group"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* QR Code in top-left corner */}
      {isPublic && timelineUrl && (
        <div className="absolute top-2 left-2 bg-white p-2 rounded-lg shadow-lg z-10">
          <QRCodeSVG
            value={timelineUrl}
            size={80}
            level="M"
            includeMargin={false}
          />
        </div>
      )}

      <button
        onClick={handleFullscreenToggle}
        className={`absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-500 hover:scale-110 z-10 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        title="Enter fullscreen"
      >
        <Maximize2 className="w-4 h-4 text-white" />
      </button>

      <div
        className={`bg-black ${getBorderColor()} rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl border backdrop-blur-sm cursor-pointer transition-all duration-500 hover:scale-[1.02] active:scale-[0.98]`}
        onClick={() => setShowClock(!showClock)}
      >
        <div className="text-center">
          {showClock ? (
            <>
              <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-2 sm:mb-3 md:mb-4 font-mono tracking-tight drop-shadow-lg">
                {currentTime}
              </div>
              <div className="text-base sm:text-lg md:text-xl lg:text-2xl text-blue-200 font-medium">
                <div className="flex flex-col items-center gap-3">
                  {isAuthenticated && onTimeOffsetChange && (
                    <div className="flex items-center gap-2 sm:gap-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleAdjustOffset(-1, e)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                        title="Decrease by 1 second"
                      >
                        <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <div className="text-xs sm:text-sm text-gray-300 min-w-[80px] sm:min-w-[100px]">
                        Offset: {timeOffset > 0 ? '+' : ''}{timeOffset}s
                      </div>
                      <button
                        onClick={(e) => handleAdjustOffset(1, e)}
                        className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                        title="Increase by 1 second"
                      >
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      {timeOffset !== 0 && (
                        <button
                          onClick={handleResetOffset}
                          className="ml-2 px-2 py-1 sm:px-3 sm:py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-xs sm:text-sm"
                          title="Reset offset to 0"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-xl sm:text-2xl md:text-3xl text-white font-bold mb-3 transition-all duration-300">
                {description}
              </div>
              {remainingSeconds > 0 ? (
                <>
                  <div className="text-base sm:text-lg md:text-xl text-white/70 font-medium mb-3 transition-all duration-300">
                    Starts in
                  </div>
                  <div className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-mono tracking-tight drop-shadow-lg ${getCountdownTextColor()}`}>
                    {timeRemaining}
                  </div>
                </>
              ) : !noUpcomingOccurrences ? (
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-state-active transition-all duration-300">
                  Happening Now
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
